import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Drawer, IconButton, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { familyLevelImage } from '../discovery.utils';
import useFamilyLevelFacets from '../hooks/useFamilyLevelFacets';
import { discoveryViewAtom } from '../state';
import { familyLevelLabelKey } from './family-level-facets';
import FamilyLevelList from './FamilyLevelList';

interface CompactDepartmentsProps {
  open: boolean;
  path: string[];
  onClose: () => void;
  onBack: () => void;
  onSelect: (code: string) => void;
  onSeeAll: () => void;
  onEmpty: () => void;
}

export default function CompactDepartmentsProps({ open, path, onClose, onBack, onSelect, onSeeAll, onEmpty }: CompactDepartmentsProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'discovery', 'product-family', 'product-classification']);
  const discoveryView = useAtomValue(discoveryViewAtom);
  const { items, isLoading } = useFamilyLevelFacets(path);
  const rootSeeAllLabel = discoveryView === 'PRODUCTS' ? t('common:all_products') : t('discovery:all_suppliers');
  const depth = path.length;
  const headerCode = path[depth - 1];
  const headerImage = headerCode ? familyLevelImage(headerCode) : undefined;
  const headerTitle = headerCode
    ? t(familyLevelLabelKey(headerCode, depth - 1))
    : t('discovery:departments');

  //If a family has no children so select the current family as the filter
  useEffect(() => {
    if (!open || depth === 0 || isLoading || items.length > 0) {
      return;
    }

    onEmpty();
  }, [open, isLoading, items.length, path]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            boxSizing: 'border-box',
            top: 8,
            bottom: 8,
            left: 8,
            right: {
              xs: 8,
              sm: 'auto',
            },
            width: {
              xs: 'auto',
              sm: 340,
            },
            maxWidth: 'calc(100% - 16px)',
            height: 'auto',
            margin: 0,
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            borderRadius: '24px',
            backgroundColor: 'surfaceContainerLow.main',
            overflowX: 'hidden',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: depth > 0 ? 64 : 40,
        }}
      >
        {depth > 0 && (
          <IconButton onClick={onBack} aria-label={t('common:back')}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          {headerImage && <Avatar src={headerImage} size={48} />}
          <Typography variant={depth === 0 ? 'titleLarge' : 'bodyLarge'} noWrap>
            {headerTitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t('common:close')}>
          <CloseIcon />
        </IconButton>
      </Box>
      <FamilyLevelList
        items={items}
        isLoading={isLoading}
        depth={depth}
        onSelect={onSelect}
        onSeeAll={onSeeAll}
        seeAllLabel={depth === 0 ? rootSeeAllLabel : undefined}
      />
    </Drawer>
  );
}
